import { useEffect, useState} from 'react';
import styles from './myAccount.module.scss';
import { Skeleton } from '@mui/material';
import { MyAccountProps } from '../../../interfaces/components';
import { images } from '../../../constants/images';
import { formatRut, validateRut } from '../../../helpers/app';
import { useProfileRole, useToast } from "../../../helpers/hooks";
import ProfileCard from "../../../components/ProfileCard";
import TextFieldShared from "../../../components/TextField/TextField";
import ButtonShared from "../../../components/Button/Button";
import Modal from '../../../components/Modal/Modal';
import Compressor from 'compressorjs';
import PhoneInput from '../../../components/PhoneInput/PhoneInput';

const MyAccount = ({
    user,
    token,
    getConcessionary,
    concessionary,
    editUserReducer,
    editUserRequest,
    cleanProfileFetching,
    postImage,
    postUserImage,
    cleanPostImage,
    deleteUser,
    deleteFetching,
    logoutUser,
    reSendValidationFetching,
    reSendValidation,
    cleanReSendValidation
}: MyAccountProps) => {
    const toast = useToast();
    const profileRole = useProfileRole();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [imageProfile, setImageProfile] = useState<string>("");
    const [newImageProfile, setNewImageProfile] = useState<any>({path: "", file: ""});

    const [name, setName] = useState<any>({value: "", error: ""});
    const [lastName, setLastName] = useState<any>({value: "", error: ""});
    const [email, setEmail] = useState<any>({value: "", error: ""});
    const [rut, setRut] = useState<any>({value: "", error: ""});
    const [phoneValue, setPhoneValue] = useState<any>({code: "", number: "", error: ""});
    const [regionSelected, setRegionSelected] = useState<any>({value: "", error: ""});
    const [citySelected, setCitySelected] = useState<any>({value: "", error: ""});
    const [communeSelected, setCommuneSelected] = useState<any>({value: "", error: ""});

    const handleClickReSendValidation = () => {
        reSendValidation();
    }

    const handleChangeImage = (event: any) => {
        const file = event.target.files[0];
        new Compressor(file, {
            success(result) {
                setNewImageProfile({
                    path: URL.createObjectURL(result),
                    file: result
                });
            },
            error(err){
                toast("Ha ocurrido un error al procesar la imagen");
            }
        });
    }

    const handleChangePhone = (value: any, type: string) => {
        if(type === "code"){
            setPhoneValue({...phoneValue, code: value});
        }else{
            setPhoneValue({...phoneValue, number: value});
        }
    }

    const handleChangeRut = (value: any) => {
        const formatValue = formatRut(value);
        setRut({...rut, value: formatValue});
    }

    const updateDataUser = () => {
        const phone = {
            code: phoneValue?.code,
            number: phoneValue?.number
        }

        let picture = user?.picture;
        if(postImage?.data.length !== 0){
            picture = postImage?.data?.path;
        }
        
        editUserRequest({
            ...user,
            name: name?.value,
            last_name: lastName?.value,
            email: email?.value,
            legal_id: formatRut(rut?.value),
            phone: phone,
            picture: picture
        });
    }

    const handleSave = () => {
        if(!name?.value) return setName({...name, error: "Debes ingresar un nombre"});
        if(!lastName?.value) return setLastName({...lastName, error: "Debes ingresar un apellido"});
        if(!email?.value) return setEmail({...email, error: "Debes ingresar un email"});
        if(!rut?.value) return setRut({...rut, error: "Debes ingresar un rut"});
        if(!phoneValue?.code) {return setPhoneValue({...phoneValue, error: "Debes ingresar un pais"})} else setPhoneValue({...phoneValue, error: ""});
        if(!phoneValue?.number) {return setPhoneValue({...phoneValue, error: "Debes ingresar un telefono"})} else setPhoneValue({...phoneValue, error: ""});

        if(!validateRut(rut?.value)) {
            return setRut({...rut, error: "Ingresa un rut valido"});
        }

        setLoading(true);

        if(!newImageProfile?.path) {
            updateDataUser();
        }

        if(newImageProfile?.path) {
            const formData = new FormData();
            formData.append("file", newImageProfile?.file, newImageProfile?.file?.name);
            postUserImage(formData, token);
        }
    }

    useEffect(() => {
        if(reSendValidationFetching === "failure"){
            toast("Ha ocurrido un error al enviar el email de verificación");
            cleanReSendValidation();
        }
        if(reSendValidationFetching === "success"){
            toast("Se te ha enviado un email de verificación");
            cleanReSendValidation();
        }
    }, [reSendValidationFetching]);

    useEffect(() => {
        if(deleteFetching === "loading"){
            setLoadingDeleteButton(true);
        }
        if(deleteFetching === "failure"){
            setLoadingDeleteButton(false);
            toast("Ha ocurrido un error al eliminar el usuario");
        }
        if(deleteFetching === "success"){
            toast("Usuario eliminado correctamente");
            setLoadingDeleteButton(false);
            setOpenDeleteModal(false);
            logoutUser();
        }
    },[deleteFetching]);

    useEffect(() => {
        if(postImage?.fetching === "failure"){
            toast("Ha ocurrido un error al actualizar la foto de perfil");
            setLoading(false);
            cleanPostImage();
        }
        if(postImage?.fetching === "success"){
            updateDataUser();
            cleanPostImage();
        }
    }, [postImage?.fetching]);

    useEffect(() => {
        if(editUserReducer === "loading"){
            setLoading(true);
        }
        if(editUserReducer === "success"){
            setLoading(false);
            toast("Cambios realizados correctamente");
            cleanProfileFetching();
        }
        if(editUserReducer === "failure"){
            setLoading(false);
            toast("Ha ocurrido un error");
            cleanProfileFetching();
        }
    }, [editUserReducer]);

    useEffect(() => {
        setName({...name, value: user?.name ? user?.name : ""});
        setLastName({...lastName, value: user?.last_name ? user?.last_name : ""});
        setEmail({...email , value: user?.email ? user?.email : ""});
        setRut({...rut , value: user?.legal_id ? formatRut(user?.legal_id) : ""});
        setPhoneValue({code: user?.phone ? user?.phone?.code : "", number: user?.phone ? user?.phone?.number : "", error: ""});
        setRegionSelected({...regionSelected , value: user?.residence?.region_id ? {id: user?.residence?.region_id, name: user?.residence?.region_name} : ""});
        setCitySelected({...citySelected , value: user?.residence?.city_id ? {id: user?.residence?.city_id, name: user?.residence?.city_name} : ""});
        setCommuneSelected({...communeSelected , value: user?.residence?.commune_id ? {id: user?.residence?.commune_id, name: user?.residence?.commune_name} : ""});
    }, [user]);

    useEffect(() => {
        if(profileRole(user?.profile?.code) === "seller" && user?.dealership && user?.profile?.code !== "admin-brand"){
            getConcessionary(user?.dealership);
        }
        if(user?.picture){
            setImageProfile(user?.picture);
        }
    }, []);

    return (
        <ProfileCard>
            <Modal open={openDeleteModal} variant="white" className={styles.deleteModalContainer}>
                <p>¿Seguro que quieres ELIMINAR tu cuenta?</p>
                <div className={styles.buttons}>
                    <ButtonShared 
                        onPress={() => setOpenDeleteModal(false)}
                        title="Atras" 
                        tertiary
                        size="small"
                    />
                    <ButtonShared 
                        onPress={() => deleteUser(user?._id)}
                        title="Eliminar" 
                        primary
                        size="small"
                        loading={loadingDeleteButton}
                    />
                </div>
            </Modal>
            <div className={styles.myAccount}>
                <div className={styles.myAccountContent}>
                    <div className={styles.imageContainer}>
                        {
                            concessionary?.fetching === "loading"
                            ? <Skeleton animation="wave" variant="circular" className={`${styles.imageProfile} ${styles.skeleton}`} />
                            : <label htmlFor="imageProfile" className={styles.imageProfile}>
                                <div className={styles.icon}>
                                    <img src={images.camera} alt="Icon" />
                                </div>
                                <input type="file" name="imageProfile" accept="image/*" onChange={handleChangeImage} />
                                <img src={newImageProfile?.path ? newImageProfile?.path : (imageProfile ? imageProfile : images.profile)} alt="Imagen perfil" />
                            </label>
                        }
                        <div className={styles.data}>
                            {
                                concessionary?.fetching === "loading"
                                ? <Skeleton animation="wave" variant="text" className={`${styles.name} ${styles.skeleton}`} />
                                : <p className={styles.name}>{`${user?.name} ${user?.last_name}`}</p>
                            }
                            {
                                concessionary?.fetching === "loading"
                                ? <Skeleton animation="wave" variant="text" className={`${styles.email} ${styles.skeleton}`} />
                                : <p className={styles.email}>{user?.email}</p>
                            }
                            {
                                !user?.email_verification?.verified 
                                ?
                                    concessionary?.fetching === "loading"
                                    ? <Skeleton animation="wave" variant="text" className={`${styles.validateEmail} ${styles.skeleton}`} />
                                    : <p className={styles.validateEmail} onClick={handleClickReSendValidation}>Aún no validaste tu mail, hazlo ahora</p>
                                :
                                    null
                            }
                        </div>
                    </div>
                    <div className={styles.line}></div>
                    <div className={styles.infoSegment}>
                        <p className={styles.title}>Datos personales</p>
                        <div className={styles.grid}>
                            <TextFieldShared
                                label="Nombre"
                                name="nameInput"
                                value={name?.value}
                                checker="onlyLetters"
                                className={styles.half}
                                onChange={(value: any) => setName({...name, value: value})}
                                loading={concessionary?.fetching === "loading"}
                                error={name?.error}
                            />
                            <TextFieldShared
                                label="Apellido"
                                name="lastNameInput"
                                checker="onlyLetters"
                                value={lastName?.value}
                                className={styles.half}
                                onChange={(value: any) => setLastName({...lastName, value: value})}
                                loading={concessionary?.fetching === "loading"}
                                error={lastName?.error}
                            />
                            <TextFieldShared
                                label="RUT"
                                name="rutInput"
                                value={rut?.value}
                                className={styles.half}
                                onChange={handleChangeRut}
                                loading={concessionary?.fetching === "loading"}
                                error={rut?.error}
                            />
                            <PhoneInput
                                label="Telefono"
                                numberValue={phoneValue.number}
                                codeValue={phoneValue.code}
                                onChange={handleChangePhone}
                                error={phoneValue?.error}
                                className={styles.half}
                            />
                        </div>
                    </div>
                    <div className={styles.line}></div>
                    <div className={styles.infoSegment}>
                        <p className={styles.title}>Sucursal</p>
                        <div className={styles.grid}>
                            <TextFieldShared
                                label="Región"
                                name="region"
                                value={regionSelected?.value && regionSelected?.value?.name}
                                disabled
                                className={styles.half}
                                loading={concessionary?.fetching === "loading"}
                            />
                            <TextFieldShared
                                label="Ciudad"
                                name="city"
                                value={citySelected?.value && citySelected?.value?.name}
                                disabled
                                className={styles.half}
                                loading={concessionary?.fetching === "loading"}
                            />
                            <TextFieldShared
                                label="Comuna"
                                name="commune"
                                value={communeSelected?.value && communeSelected?.value?.name}
                                disabled
                                className={styles.half}
                                loading={concessionary?.fetching === "loading"}
                            />
                            <TextFieldShared
                                label="Concesionaria"
                                name="concessionary"
                                value={concessionary?.data?.name}
                                disabled
                                className={styles.half}
                                loading={concessionary?.fetching === "loading"}
                            />
                        </div>
                    </div>
                    <div className={styles.buttons}>
                        <ButtonShared
                            onPress={handleSave}
                            title="Guardar cambios"
                            primary
                            loading={concessionary?.fetching === "loading" || loading}
                        />
                    </div>
                </div>
            </div>
        </ProfileCard>
    )
};
export default MyAccount;